import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const About = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>À propos du Théâtre | Les Z'Ineuvitables</title>
        <meta
          name="description"
          content="Découvrez les infos pratiques de la troupe de théâtre Les Z'Ineuvitables à Neuville-en-Ferrain, proche de Lille."
        />
      </Helmet>
      
      <MainContainer>
        <HeaderSection>
          <h1>À propos des Z'Ineuvitables</h1>
          <p className="subtitle">
            Une troupe de théâtre solidaire créée en 2008 à Neuville-en-Ferrain.
          </p>
        </HeaderSection>

        {/* Section Banners : Infos clés en un coup d'œil */}
        <InfoBar>
          <InfoItem>
            <span className="icon">🕒</span>
            <div>
              <strong>Répétitions</strong>
              <p>Chaque samedi de 10h à 12h</p>
            </div>
          </InfoItem>
          <InfoItem>
            <span className="icon">📍</span>
            <div>
              <strong>Lieu</strong>
              <p>40 rue de Tourcoing, Neuville-en-Ferrain, à côté de la ludothèque</p>
            </div>
          </InfoItem>
          <InfoItem>
            <span className="icon">❤️</span>
            <div>
              <strong>Engagement</strong>
              <p>Au profit de l'association TAZ'FIT pour 2026</p>
            </div>
          </InfoItem>
        </InfoBar>

        {/* Section Synopsis / Pièce actuelle */}
        <FeaturedSection>
          <div className="tag">Spectacle de la saison</div>
          <h2>🎭 Une comédie pleine de surprises !</h2>
          <p>
            Dans un bistrot de village, rien ne va plus ! Pas un client en vue, les factures s'empilent et la caisse sonne creux. Mais ce n'est que le calme avant la tempête... Entre révélations, mensonges et magouilles, le bistrot vire rapidement au joyeux chaos !
          </p>
        </FeaturedSection>

        {/* Section Troupe / Recrutement */}
        <GridTwoColumns>
          <ArticleCard>
            <h3>📜 Notre Histoire</h3>
            <p>
              Créée en 2008 au sein de l'Amicale Laïque Jules Ferry, notre troupe réunit aujourd'hui 11 comédiens passionnés autour du plaisir de la scène et du partage avec le public.
            </p>
          </ArticleCard>

          <ArticleCard>
            <h3>👥 Envie de nous rejoindre ?</h3>
            <p>
              Vous rêvez de monter sur les planches ? Nous recherchons régulièrement de nouveaux talents pour enrichir la mixité de la troupe et diversifier notre répertoire.
            </p>
          </ArticleCard>
        </GridTwoColumns>

        {/* Actions principales */}
        <CallToAction>
          <LinkButton to="/portfolio">Découvrir la troupe</LinkButton>
          <ExternalLink
            href="https://maps.app.goo.gl/NMEY7MnidNZnesyp6"
            target="_blank"
            rel="noopener noreferrer"
          >
            Itinéraire Google Maps 📍
          </ExternalLink>
        </CallToAction>
      </MainContainer>
    </>
  );
};

export default About;

// STYLES MODERNES ET COMPATIBLES DARK MODE
const MainContainer = styled.div`
  max-width: 850px;
  margin: 0 auto;
  padding: 85px 20px 30px;
  box-sizing: border-box;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 25px;

  h1 {
    font-size: 2rem;
    margin-bottom: 8px;
    color: inherit;
  }

  .subtitle {
    font-size: 1.05rem;
    opacity: 0.8;
    margin: 0;
  }
`;

const InfoBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;

  .icon {
    font-size: 1.5rem;
  }

  strong {
    display: block;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    opacity: 0.8;
  }
`;

const FeaturedSection = styled.div`
  position: relative;
  padding: 20px 25px;
  background: linear-gradient(135deg, rgba(0, 86, 179, 0.2) 0%, rgba(0, 0, 0, 0.3) 100%);
  border: 1px solid rgba(0, 123, 255, 0.3);
  border-radius: 12px;
  margin-bottom: 25px;

  .tag {
    display: inline-block;
    padding: 3px 10px;
    background: #0056b3;
    color: white;
    font-size: 0.75rem;
    font-weight: bold;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
  }

  h2 {
    margin: 0 0 10px 0;
    font-size: 1.3rem;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    opacity: 0.9;
  }
`;

const GridTwoColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
`;

const ArticleCard = styled.div`
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;

  h3 {
    margin: 0 0 8px 0;
    font-size: 1.1rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.45;
    opacity: 0.85;
  }
`;

const CallToAction = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  padding-top: 10px;
`;

const buttonStyle = `
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.2s ease;
`;

const LinkButton = styled(Link)`
  ${buttonStyle}
  background-color: #0056b3;
  color: white;
  &:hover {
    background-color: #004085;
    transform: translateY(-2px);
  }
`;

const ExternalLink = styled.a`
  ${buttonStyle}
  background: transparent;
  color: inherit;
  border: 1px solid rgba(255, 255, 255, 0.3);
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;